import { useId, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import cx from "clsx"
import type { ProgramChannels, RolloutCoverage, RolloutLocation } from "shared"
import {
  fetchRolloutLocations,
  fetchLocationRollout,
  saveLocationRollout,
} from "../api"
import ArrowIcon from "../../../assets/arrow.svg?react"
import { AdminButton } from "@rawa/ui/AdminButton"
import { Checkbox } from "@rawa/ui/Checkbox"
import { DashMark, TickMark } from "@rawa/ui/CheckboxMarks"
import { ErrorCallout } from "@rawa/ui/ErrorCallout"
import { NOT_AUTHORIZED_MESSAGE } from "../utils/notAuthorized"
import { isForbidden } from "@rawa/api-client/ApiResponseError"
import { trackError } from "@rawa/telemetry"
import typography from "@rawa/ui/styles/typography.module.css"
import styles from "./RolloutPage.module.scss"

// How each location's rollout summary is announced. The dropdown shows it as a
// checkbox — empty, dashed or ticked — which says nothing to a screen reader.
const COVERAGE_LABEL: Record<RolloutCoverage, string> = {
  none: "no programs on",
  partial: "some programs on",
  all: "all programs on",
}

// Equal by channel state, ignoring order and display name.
const sameChannels = (a: ProgramChannels[], b: ProgramChannels[]) => {
  if (a.length !== b.length) return false
  const byId = new Map(b.map((program) => [program.id, program]))
  return a.every((program) => {
    const other = byId.get(program.id)
    return (
      other?.notificationsEnabled === program.notificationsEnabled &&
      other?.kioskEnabled === program.kioskEnabled &&
      other?.challengeEnabled === program.challengeEnabled
    )
  })
}

type SaveRequest = { location: RolloutLocation; programs: ProgramChannels[] }

// Which programs are live at a location, and on which channel. The location's
// whole set is edited locally and saved in one action, so a half-applied rollout
// is never sent.
export function RolloutPage() {
  const queryClient = useQueryClient()
  const [selected, setSelected] = useState<RolloutLocation | null>(null)
  // What the admin has made of the selected location's rollout, until it is
  // saved or cancelled. Null while nothing has been changed.
  const [edited, setEdited] = useState<ProgramChannels[] | null>(null)
  const [open, setOpen] = useState(false)

  const labelId = useId()
  const listboxId = useId()

  // The client's default retries exist for a kiosk tablet waking its Wi-Fi. An
  // administrator at a desk is told what went wrong instead. Nothing here is
  // re-read in the background; a save refreshes what it changed.
  const summaries = useQuery({
    queryKey: ["rollout-locations"],
    queryFn: fetchRolloutLocations,
    staleTime: Infinity,
    retry: false,
    meta: { source: "fetchRolloutLocations", forbiddenIsExpected: true },
  })
  const locations = summaries.data ?? []

  // Keyed by location, so a slow answer for a location the admin has moved on
  // from never shows under the one chosen after it. Read afresh each time a
  // location is chosen, because a save writes its whole set back.
  const rollout = useQuery({
    queryKey: ["location-rollout", selected?.id],
    queryFn: () => fetchLocationRollout(selected!.id),
    enabled: selected !== null,
    staleTime: Infinity,
    gcTime: 0,
    retry: false,
    meta: { source: "fetchLocationRollout", forbiddenIsExpected: true },
  })
  const loaded = rollout.data ?? null
  const shown = edited ?? loaded
  const loadingPrograms = selected !== null && rollout.isPending

  // A list that loaded once stays up if refreshing it after a save fails.
  const loadFailure =
    (summaries.isLoadingError ? summaries.error : null) ?? rollout.error
  const forbidden = isForbidden(loadFailure)
  const loadError = loadFailure !== null && !forbidden

  const save = useMutation({
    mutationFn: ({ location, programs }: SaveRequest) =>
      saveLocationRollout(
        location.id,
        programs.map(
          ({ id, notificationsEnabled, kioskEnabled, challengeEnabled }) => ({
            id,
            notificationsEnabled,
            kioskEnabled,
            challengeEnabled,
          }),
        ),
      ),
    onError: (err) => trackError(err, { source: "saveLocationRollout" }),
    onSuccess: (_, { location, programs }) => {
      // Only now are the edits what the server holds; a failed save leaves them
      // on screen to retry.
      queryClient.setQueryData(["location-rollout", location.id], programs)
      setEdited(null)
      // The list summarises each location's coverage, which this save has
      // just changed.
      return queryClient.invalidateQueries({ queryKey: ["rollout-locations"] })
    },
  })
  const saving = save.isPending

  function choose(location: RolloutLocation) {
    setOpen(false)
    setSelected(location)
    setEdited(null)
    save.reset()
  }

  // Compared by value, not tracked with a flag, so toggling a channel and
  // toggling it back leaves nothing to save.
  const dirty =
    edited !== null && loaded !== null && !sameChannels(edited, loaded)

  function setChannel(
    programId: string,
    channel: "notificationsEnabled" | "kioskEnabled" | "challengeEnabled",
    enabled: boolean,
  ) {
    setEdited(
      (current) =>
        (current ?? loaded)?.map((program) =>
          program.id === programId
            ? { ...program, [channel]: enabled }
            : program,
        ) ?? null,
    )
  }

  return (
    <div className={styles.page}>
      <div className={styles.titleBlock}>
        <h1 className={cx(typography.h2, styles.heading)}>Rollout</h1>
        <p className={styles.subtitle}>
          To activate client feedback, select the location, then the feedback
          channels to activate per program.
        </p>
      </div>

      {forbidden && <ErrorCallout>{NOT_AUTHORIZED_MESSAGE}</ErrorCallout>}
      {loadError && <ErrorCallout>Failed to load the rollout.</ErrorCallout>}

      {summaries.isPending && (
        <div
          role="status"
          aria-label="Loading locations"
          className={styles.field}
        >
          <span className={styles.skeletonLabel} />
          <span className={styles.skeletonTrigger} />
        </div>
      )}

      {!summaries.isPending && !forbidden && (
        <div className={styles.field}>
          <span id={labelId} className={styles.label}>
            Location selection <span className={styles.required}>*</span>
          </span>
          {/* Select-only combobox (WAI-ARIA APG): the trigger carries the role
              and is named by the label, while its text content is the current
              value. A plain button would give screen readers the value as the
              name and no indication the field is a choice. */}
          <button
            type="button"
            role="combobox"
            className={styles.trigger}
            aria-labelledby={labelId}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-controls={open ? listboxId : undefined}
            onClick={() => setOpen((isOpen) => !isOpen)}
          >
            <span className={selected ? styles.value : styles.placeholder}>
              {selected?.name ?? "Select the location"}
            </span>
            <span className={styles.chevronBox} aria-hidden={true}>
              <ArrowIcon
                className={styles.chevronDown}
                width={20}
                height={20}
              />
            </span>
          </button>
          {open && (
            <div
              role="listbox"
              id={listboxId}
              aria-labelledby={labelId}
              className={styles.listbox}
            >
              {locations.map((location) => (
                <button
                  key={location.id}
                  type="button"
                  role="option"
                  aria-selected={location.id === selected?.id}
                  // The indicator below is decorative, so the coverage it shows
                  // has to reach a screen reader through the option's name.
                  aria-label={`${location.name}, ${COVERAGE_LABEL[location.coverage]}`}
                  className={styles.option}
                  onClick={() => choose(location)}
                >
                  <span
                    className={styles.coverage}
                    data-coverage={location.coverage}
                    aria-hidden={true}
                  >
                    {location.coverage === "partial" ? (
                      <DashMark className={styles.coverageMark} />
                    ) : (
                      <TickMark className={styles.coverageMark} />
                    )}
                  </span>
                  <span className={styles.optionName}>{location.name}</span>
                  <ArrowIcon
                    className={styles.optionChevron}
                    width={16}
                    height={16}
                    aria-hidden={true}
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {loadingPrograms && (
        <div
          role="status"
          aria-label="Loading programs"
          className={styles.panel}
        >
          {[0, 1, 2].map((i) => (
            <div key={i} className={styles.skeletonRow}>
              <span className={styles.skeletonLabel} />
            </div>
          ))}
        </div>
      )}

      {shown?.length === 0 && (
        <p className={styles.empty}>
          No programs to configure at this location.
        </p>
      )}

      {shown && shown.length > 0 && (
        <>
          {save.isError && (
            <ErrorCallout>
              {"Couldn't save the rollout. Please try again."}
            </ErrorCallout>
          )}

          <table className={styles.table}>
            <thead>
              <tr>
                <th scope="col" className={styles.columnHeader}>
                  Program
                </th>
                <th
                  scope="col"
                  colSpan={3}
                  className={cx(styles.columnHeader, styles.channelsHeader)}
                >
                  Channels
                </th>
              </tr>
            </thead>
            <tbody>
              {shown.map((program) => (
                <tr key={program.id}>
                  <th scope="row" className={styles.programName}>
                    {program.name}
                  </th>
                  <td className={styles.channelCell}>
                    <Checkbox
                      checked={program.notificationsEnabled}
                      label="Notifications"
                      disabled={saving}
                      onChange={(enabled) =>
                        setChannel(program.id, "notificationsEnabled", enabled)
                      }
                      ariaLabel={`${program.name} notifications`}
                    />
                  </td>
                  <td className={styles.channelCell}>
                    <Checkbox
                      checked={program.kioskEnabled}
                      label="Kiosk"
                      disabled={saving}
                      onChange={(enabled) =>
                        setChannel(program.id, "kioskEnabled", enabled)
                      }
                      ariaLabel={`${program.name} kiosk`}
                    />
                  </td>
                  <td className={styles.channelCell}>
                    <Checkbox
                      checked={program.challengeEnabled}
                      label="Challenge"
                      disabled={saving}
                      onChange={(enabled) =>
                        setChannel(program.id, "challengeEnabled", enabled)
                      }
                      ariaLabel={`${program.name} challenge`}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className={styles.actions}>
            <AdminButton
              variant="outline"
              onClick={() => {
                setEdited(null)
                save.reset()
              }}
              disabled={saving}
            >
              Cancel
            </AdminButton>
            <AdminButton
              onClick={() =>
                save.mutate({ location: selected!, programs: edited! })
              }
              disabled={!dirty || saving}
            >
              {saving ? "Saving…" : "Save"}
            </AdminButton>
          </div>
        </>
      )}
    </div>
  )
}
